import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TiptapLink from '@tiptap/extension-link';
import { Pencil } from 'lucide-react';
import { useLiveEditorScope } from '../../context/LiveEditorScopeContext';
import UniversalBubbleMenu from './UniversalBubbleMenu';

interface EditableRichTextProps {
  value: string;
  onChange: (newHtml: string) => void;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div' | 'span';
  className?: string;
  placeholder?: string;
  multiline?: boolean;
  allowHeadings?: boolean;
  label?: string;
  style?: React.CSSProperties;
  canEdit?: boolean;
}

export default function EditableRichText({
  value,
  onChange,
  as: Component = 'div',
  className = '',
  placeholder = 'Sorot teks untuk memformat...',
  multiline = false,
  allowHeadings = false,
  label,
  style = {},
  canEdit: canEditProp
}: EditableRichTextProps) {
  const scope = useLiveEditorScope();
  const canEdit = typeof canEditProp === 'boolean' ? canEditProp : scope.canEdit;
  const [isFocused, setIsFocused] = useState(false);
  const lastEmittedValueRef = useRef(value);

  const isLightText =
    className.includes('text-white') ||
    className.includes('text-[#F9F9F9]') ||
    className.includes('text-[#f9f9f9]') ||
    className.includes('text-slate-100') ||
    className.includes('text-gray-100') ||
    className.includes('text-amber-100') ||
    className.includes('text-white/70');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: allowHeadings ? { levels: [2, 3] } : false,
        bulletList: multiline ? {} : false,
        orderedList: multiline ? {} : false,
        blockquote: multiline ? {} : false,
        link: false,
        underline: false
      }),
      Underline,
      TextStyle,
      Color,
      TiptapLink.configure({
        openOnClick: false,
        autolink: false,
        HTMLAttributes: {
          class: isLightText
            ? 'text-[#E6C687] underline hover:text-[#f3d99f] transition-colors'
            : 'text-brand-wine underline hover:text-brand-wine-deep transition-colors'
        }
      })
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: `outline-none focus:outline-none min-h-[1em] ${multiline ? '[&_p]:mb-2 last:[&_p]:mb-0' : '[&_p]:m-0 [&_p]:inline'} font-inherit text-inherit`
      }
    },
    onUpdate: ({ editor }) => {
      let html = editor.getHTML();
      // If single line, clean unnecessary empty wrapping tags if present
      if (!multiline && html === '<p></p>') {
        html = '';
      }
      lastEmittedValueRef.current = html;
      onChange(html);
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => {
      setIsFocused(false);
    }
  });

  // Sync external value updates (e.g. discard draft, API load)
  useEffect(() => {
    if (editor && value !== undefined && value !== lastEmittedValueRef.current) {
      if (editor.getHTML() !== value) {
        editor.commands.setContent(value || '', { emitUpdate: false });
        lastEmittedValueRef.current = value;
      }
    }
  }, [value, editor]);

  if (!canEdit) {
    return (
      <Component
        className={`${className} [text-align:inherit]`}
        style={style}
        dangerouslySetInnerHTML={{ __html: value || placeholder }}
      />
    );
  }

  const focusStyles = isLightText
    ? `bg-transparent hover:bg-white/10 focus-within:bg-black/30 backdrop-blur-xs border border-dashed ${
        isFocused ? 'border-[#E6C687] ring-2 ring-[#E6C687]/40' : 'border-white/30 hover:border-white/70'
      } caret-[#E6C687]`
    : `bg-transparent hover:bg-brand-wine/[0.05] focus-within:bg-white/70 backdrop-blur-xs border border-dashed ${
        isFocused ? 'border-brand-wine ring-2 ring-brand-wine/20' : 'border-brand-wine/30 hover:border-brand-wine/70'
      } caret-brand-wine`;

  return (
    <div className="relative group w-full max-w-full [text-align:inherit]">
      {label && (
        <span
          className={`absolute -top-3.5 left-1 z-20 text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded shadow opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${
            isLightText ? 'bg-[#1C1817] text-[#E6C687] border border-white/20' : 'bg-brand-wine text-white'
          }`}
        >
          {label} (Rich Text)
        </span>
      )}

      {/* Reusable Universal TipTap Floating Bubble Menu */}
      <UniversalBubbleMenu
        editor={editor}
        allowHeadings={allowHeadings}
        allowLists={multiline}
        allowQuote={multiline}
        allowColors={true}
        allowLink={true}
      />

      {/* Editor Content Area */}
      <div className={`w-full rounded px-2 py-0.5 transition-all cursor-text [text-align:inherit] ${focusStyles}`}>
        <EditorContent
          editor={editor}
          className={`${className} [text-align:inherit] [&_.ProseMirror]:outline-none [&_.ProseMirror]:font-inherit [&_.ProseMirror]:text-inherit [&_.ProseMirror]:text-[length:inherit] [&_.ProseMirror]:leading-[inherit] [&_.ProseMirror]:[text-align:inherit] [&_.ProseMirror_p]:font-inherit [&_.ProseMirror_p]:text-inherit [&_.ProseMirror_p]:text-[length:inherit] [&_.ProseMirror_p]:leading-[inherit] [&_.ProseMirror_p]:[text-align:inherit] ${
            !multiline ? '[&_.ProseMirror_p]:inline [&_.ProseMirror_p]:m-0' : '[&_.ProseMirror_p]:m-0'
          }`}
        />
      </div>

      <div className="absolute right-1 top-2 pointer-events-none opacity-0 group-hover:opacity-70 transition-opacity">
        <Pencil className={`w-3.5 h-3.5 ${isLightText ? 'text-[#E6C687]' : 'text-brand-wine'}`} />
      </div>
    </div>
  );
}
